import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, ArrowLeft, ArrowRight, ClipboardPaste, Search, MapPin } from "lucide-react";

export const PLACE_TYPES = ["City", "Town", "Village", "Beach", "Hill Station", "Wildlife Reserve",
  "Heritage Destination", "Pilgrimage Destination", "Nature Destination", "Other"];

// ---- shared places cache ----
const PlacesCtx = createContext(null);
export function PlacesProvider({ children }) {
  const [places, setPlaces] = useState([]);
  const reload = useCallback(() => api.get("/admin/places").then((r) => setPlaces(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return <PlacesCtx.Provider value={{ places, reload }}>{children}</PlacesCtx.Provider>;
}
export const usePlaces = () => useContext(PlacesCtx) || { places: [], reload: () => {} };

const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
const asRef = (p) => ({ place_id: p.id, name: p.name, slug: p.slug, type: p.type, district: p.district });

function parsePasted(text) {
  return text.split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•·]|\d+[.)])\s*/, "").trim()).filter(Boolean);
}

// ---- inline create / duplicate dialog ----
export function NewPlaceDialog({ open, initialName = "", onClose, onCreated }) {
  const { reload } = usePlaces();
  const [f, setF] = useState({ name: "", type: "Other", district: "", region: "" });
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setF({ name: initialName, type: "Other", district: "", region: "" }); setDup(null); } }, [open, initialName]);

  const checkDup = async (name) => {
    if (!name.trim()) { setDup(null); return; }
    try { const { data } = await api.get("/admin/places/check-duplicate", { params: { name } }); setDup(data.duplicate || null); }
    catch { setDup(null); }
  };
  const create = async (force = false) => {
    if (!f.name.trim()) { toast.error("Place name is required"); return; }
    setSaving(true);
    try { const { data } = await api.post(`/admin/places?force=${force}`, f); toast.success("Place created"); await reload(); onCreated(data); onClose(); }
    catch (e) {
      if (e.response?.status === 409) { setDup(e.response.data.detail?.existing || null); toast.error("This place already exists"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-place-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Add Destination / Place</DialogTitle></DialogHeader>
        {dup && (
          <div className="rounded-lg border border-saffron/40 bg-saffron/10 p-3 text-sm" data-testid="place-duplicate-warning">
            <div className="font-semibold text-saffron">Possible duplicate: {dup.name}</div>
            <div className="text-xs text-muted-foreground">{[dup.type, dup.district, dup.region].filter(Boolean).join(" · ")}</div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" data-testid="use-existing-place" onClick={() => { onCreated(dup); onClose(); }} className="rounded-full bg-forest text-white hover:bg-forest/90">Use Existing</Button>
              <Button size="sm" variant="outline" data-testid="continue-create-place" onClick={() => create(true)} className="rounded-full">Create New Anyway</Button>
            </div>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs font-semibold">Place Name *</Label><Input data-testid="np-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} onBlur={(e) => checkDup(e.target.value)} /></div>
          <div><Label className="text-xs font-semibold">Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="np-type"><SelectValue /></SelectTrigger><SelectContent>{PLACE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs font-semibold">District</Label><Input data-testid="np-district" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold">Linked Region (optional)</Label><Input data-testid="np-region" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="np-save" disabled={saving || !!dup} onClick={() => create(false)} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />Create Place</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- search dropdown (shared) ----
function PlaceSearch({ onPick, onCreate, placeholder, testid }) {
  const { places } = usePlaces();
  const [q, setQ] = useState("");
  const [openList, setOpenList] = useState(false);
  const matches = q.trim() ? places.filter((p) => norm(p.name).includes(norm(q))).slice(0, 8) : [];
  const exact = places.find((p) => norm(p.name) === norm(q));
  const addCurrent = () => {
    if (!q.trim()) return;
    if (exact) { onPick(exact); setQ(""); setOpenList(false); }
    else onCreate(q.trim());
  };
  return (
    <div className="relative">
      <div className="flex gap-1">
        <div className="relative flex-1"><Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid={testid} className="pl-8" placeholder={placeholder} value={q}
            onChange={(e) => { setQ(e.target.value); setOpenList(true); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCurrent(); } }}
            onFocus={() => setOpenList(true)} onBlur={() => setTimeout(() => setOpenList(false), 150)} />
        </div>
        {q.trim() && !exact && <Button type="button" size="sm" variant="outline" data-testid={`${testid}-create`} onClick={() => onCreate(q.trim())} className="rounded-full text-xs whitespace-nowrap"><Plus className="mr-1 h-3 w-3" />New</Button>}
      </div>
      {openList && matches.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
          {matches.map((p) => (
            <button key={p.id} type="button" data-testid={`place-opt-${p.slug}`} onMouseDown={(e) => { e.preventDefault(); onPick(p); setQ(""); setOpenList(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream">
              <MapPin className="h-3.5 w-3.5 text-saffron" /><span className="font-medium text-charcoal">{p.name}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{p.type}{p.district ? ` · ${p.district}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- ordered multi-select (Destinations / Places Covered, Places Visited) ----
export function PlaceMultiSelect({ value = [], onChange, placeholder = "Search or add a place, press Enter…", testid = "place-multi", allowPaste = true }) {
  const [newFor, setNewFor] = useState(null); // typed name to create
  const [showPaste, setShowPaste] = useState(false);
  const add = (p) => {
    const ref = asRef(p);
    if (value.some((v) => v.place_id === ref.place_id)) { toast.info(`${ref.name} is already added`); return; }
    onChange([...value, ref]);
  };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= value.length) return; const a = [...value]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div data-testid={testid}>
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-cream/40 p-2" data-testid={`${testid}-chips`}>
        {value.length === 0 && <span className="px-1 text-xs text-muted-foreground">No places selected yet.</span>}
        {value.map((p, i) => (
          <span key={`${p.place_id}-${i}`} className="flex items-center gap-1 rounded-full border border-gold/40 bg-white py-1 pl-3 pr-1 text-xs text-charcoal" data-testid={`chip-${p.slug}`}>
            <span className="font-accent text-[10px] text-saffron">{i + 1}</span>{p.name}
            <button type="button" onClick={() => move(i, -1)} className="ml-1 text-muted-foreground hover:text-charcoal" title="Move left"><ArrowLeft className="h-3 w-3" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-muted-foreground hover:text-charcoal" title="Move right"><ArrowRight className="h-3 w-3" /></button>
            <button type="button" data-testid={`chip-remove-${p.slug}`} onClick={() => remove(i)} className="text-maroon hover:opacity-70"><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-start gap-1">
        <div className="flex-1"><PlaceSearch onPick={add} onCreate={setNewFor} placeholder={placeholder} testid={`${testid}-search`} /></div>
        {allowPaste && <Button type="button" size="sm" variant="outline" data-testid={`${testid}-paste`} onClick={() => setShowPaste(true)} className="rounded-full text-xs"><ClipboardPaste className="mr-1 h-3 w-3" />Paste</Button>}
      </div>
      <NewPlaceDialog open={!!newFor} initialName={newFor || ""} onClose={() => setNewFor(null)} onCreated={(p) => add(p)} />
      {showPaste && <PastePlacesDialog onClose={() => setShowPaste(false)} onDone={(refs) => { const merged = [...value]; refs.forEach((r) => { if (!merged.some((m) => m.place_id === r.place_id)) merged.push(r); }); onChange(merged); setShowPaste(false); }} />}
    </div>
  );
}

// ---- single select (Starting / Ending / Overnight) ----
export function PlaceSingleSelect({ value, onChange, placeholder = "Search or add…", testid = "place-single", clearable = false, clearLabel = "Clear" }) {
  const [newFor, setNewFor] = useState(null);
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm" data-testid={testid}>
        <MapPin className="h-3.5 w-3.5 text-saffron" /><span className="font-medium text-charcoal">{value.name}</span>
        <button type="button" data-testid={`${testid}-change`} onClick={() => onChange(null)} className="ml-auto text-xs text-saffron hover:underline">Change</button>
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      <PlaceSearch onPick={(p) => onChange(asRef(p))} onCreate={setNewFor} placeholder={placeholder} testid={`${testid}-search`} />
      {clearable && <button type="button" data-testid={`${testid}-none`} onClick={() => onChange({ none: true })} className="mt-1 text-[11px] text-muted-foreground hover:text-charcoal">{clearLabel}</button>}
      <NewPlaceDialog open={!!newFor} initialName={newFor || ""} onClose={() => setNewFor(null)} onCreated={(p) => onChange(asRef(p))} />
    </div>
  );
}

// ---- paste multiple ----
function PastePlacesDialog({ onClose, onDone }) {
  const { places, reload } = usePlaces();
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState(null); // [{name, match|null}]
  const [creatingIdx, setCreatingIdx] = useState(null);
  const parse = () => {
    const names = parsePasted(raw);
    setRows(names.map((n) => ({ name: n, match: places.find((p) => norm(p.name) === norm(n)) || null })));
  };
  const finish = () => {
    const refs = rows.filter((r) => r.match).map((r) => asRef(r.match));
    if (refs.length === 0) { toast.error("No places matched. Create them first or match to existing."); return; }
    onDone(refs);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="paste-places-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Paste Multiple Destinations</DialogTitle></DialogHeader>
        {!rows ? (
          <>
            <Textarea rows={6} data-testid="paste-textarea" placeholder={"Mumbai\nRatnagiri\nGanpatipule\nChiplun"} value={raw} onChange={(e) => setRaw(e.target.value)} />
            <DialogFooter><Button data-testid="paste-parse" onClick={parse} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Parse</Button></DialogFooter>
          </>
        ) : (
          <>
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`paste-row-${i}`}>
                  <span className="flex-1 text-charcoal">{r.name}</span>
                  {r.match ? <span className="text-xs text-forest">✓ {r.match.name}</span>
                    : <Button size="sm" variant="outline" data-testid={`paste-create-${i}`} onClick={() => setCreatingIdx(i)} className="rounded-full text-xs"><Plus className="mr-1 h-3 w-3" />Create</Button>}
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setRows(null)} className="rounded-full">Back</Button><Button data-testid="paste-add-all" onClick={finish} className="rounded-full bg-forest text-white hover:bg-forest/90">Add Matched</Button></DialogFooter>
            <NewPlaceDialog open={creatingIdx !== null} initialName={creatingIdx !== null ? rows[creatingIdx].name : ""} onClose={() => setCreatingIdx(null)}
              onCreated={async (p) => { await reload(); setRows((rs) => rs.map((r, j) => j === creatingIdx ? { ...r, match: p } : r)); setCreatingIdx(null); }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
